import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Check, 
  Zap, 
  CreditCard,
  Loader2,
  ArrowRight,
  Crown,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    id: 'trial',
    name: '30-Day Trial',
    price: 0,
    period: 'for 30 days',
    features: ['2 channel monitors', '10 alerts/month', 'Email notifications', 'Basic keyword filters'],
    monitors: 2,
    alerts: 10
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    period: '/month',
    features: ['5 channel monitors', '200 alerts/month', 'All notification types', 'Smart keyword matching', 'Priority support'],
    monitors: 5,
    alerts: 200,
    popular: true
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 39,
    period: '/month',
    features: ['20 channel monitors', '500 alerts/month', 'All notification types', 'AI-powered matching', 'Dedicated support'],
    monitors: 20,
    alerts: 500
  }
];

export default function BillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 5;
      
      const pollStatus = async () => {
        try {
          const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
          
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Your plan has been upgraded.');
            navigate('/billing', { replace: true });
            fetchSubscription();
            return;
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired');
            navigate('/billing', { replace: true });
            return;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            toast.info('Payment is being processed...');
            navigate('/billing', { replace: true });
          }
        } catch (error) {
          navigate('/billing', { replace: true });
        }
      };
      
      pollStatus();
    }
  }, [location.search, navigate, fetchSubscription]);

  useEffect(() => {
    fetchSubscription();
    checkPaymentStatus();
  }, [fetchSubscription, checkPaymentStatus]);

  const handleUpgrade = async (planId) => {
    if (planId === 'free') return;
    
    setProcessing(planId);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        plan: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessing(null);
    }
  };

  const currentPlan = plans.find(p => p.id === (subscription?.plan || 'trial'));
  const usagePercent = subscription ? Math.round((subscription.alerts_used || 0) / (subscription.alerts_limit || 10) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="billing-page-title">
            Billing & Plans
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your subscription and view usage
          </p>
        </motion.div>

        {/* Current Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Current Plan
                  </CardTitle>
                  <CardDescription>
                    Your subscription details and usage
                  </CardDescription>
                </div>
                <Badge variant={subscription?.plan === 'trial' ? 'secondary' : 'default'} className="px-3 py-1">
                  {subscription?.plan === 'trial' ? (
                    '30-Day Trial'
                  ) : (
                    <><Crown className="w-3 h-3 mr-1" /> {currentPlan?.name}</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="text-xl font-bold">{currentPlan?.name}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-muted-foreground">Monitors</p>
                  <p className="text-xl font-bold">{subscription?.monitors_limit || 2}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-muted-foreground">Alerts/month</p>
                  <p className="text-xl font-bold">{subscription?.alerts_limit || 50}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Alerts used this month</span>
                  <span className="font-medium">{subscription?.alerts_used || 0} / {subscription?.alerts_limit || 10}</span>
                </div>
                <Progress value={usagePercent} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Plans Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-xl font-bold font-['Outfit'] mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan, index) => {
              const isCurrent = plan.id === (subscription?.plan || 'trial');
              const isUpgrade = plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.id === (subscription?.plan || 'trial'));
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <Card className={`h-full relative ${plan.popular ? 'border-primary shadow-lg shadow-primary/10' : 'border-border/50'} ${isCurrent ? 'ring-2 ring-primary' : ''}`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Popular
                        </Badge>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute -top-3 right-4">
                        <Badge variant="outline" className="bg-background">
                          Current
                        </Badge>
                      </div>
                    )}
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="mb-4">
                        <h3 className="font-bold text-lg">{plan.name}</h3>
                        <div className="flex items-baseline gap-1 mt-2">
                          <span className="text-3xl font-bold font-['Outfit']">${plan.price}</span>
                          <span className="text-muted-foreground text-sm">{plan.period}</span>
                        </div>
                      </div>
                      
                      <ul className="space-y-2 flex-1 mb-6">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={isCurrent || !isUpgrade || processing !== null}
                        variant={plan.popular && isUpgrade ? 'default' : 'outline'}
                        className="w-full rounded-full"
                        data-testid={`upgrade-${plan.id}`}
                      >
                        {processing === plan.id && <Loader2 className="w-4 h-4 animate-spin" />}
                        {processing !== plan.id && isCurrent && 'Current Plan'}
                        {processing !== plan.id && !isCurrent && isUpgrade && (
                          <>Upgrade <ArrowRight className="w-4 h-4 ml-1" /></>
                        )}
                        {processing !== plan.id && !isCurrent && !isUpgrade && 'Contact Sales'}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
